import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, extractErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, Check, AlertTriangle } from "lucide-react";

const CAT_LABEL = {
  onboarding_reminders: "Onboarding reminders",
  job_opportunities: "New job opportunities",
  announcements: "Announcements",
  product_updates: "Product updates",
  digests: "Digests",
};

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const initCat = params.get("cat") || "";
  const [info, setInfo] = useState(null);
  const [scope, setScope] = useState("category");
  const [category, setCategory] = useState(CAT_LABEL[initCat] ? initCat : "onboarding_reminders");
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/email/unsub-info", { params: { t } })
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(extractErrorMessage(e, "This link is invalid or has expired.")));
  }, [t]);

  const submit = async (resub) => {
    setBusy(true);
    try {
      const path = resub ? "/email/resubscribe" : "/email/unsubscribe";
      await api.post(path, { t, scope, category });
      setDone(resub ? "resubscribed" : "unsubscribed");
    } catch (e) {
      setError(extractErrorMessage(e, "Something went wrong."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6" data-testid="unsubscribe-page">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-slate-900">
          <Mail className="h-6 w-6 text-indigo-500" />
          <span className="font-display text-xl font-bold">Email preferences</span>
        </div>

        {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700" data-testid="unsub-error"><AlertTriangle className="mr-1 inline h-4 w-4" />{error}</div>}

        {done && (
          <div className="space-y-3" data-testid="unsub-done">
            <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800"><Check className="mr-1 inline h-4 w-4" />
              You're {done === "resubscribed" ? "resubscribed" : "unsubscribed"}{scope === "all" ? " from all optional email" : ` from ${CAT_LABEL[category] || category}`}. This takes effect immediately.
            </div>
            {done === "unsubscribed" && category === "job_opportunities" && (
              <p className="text-xs text-slate-500">You won't receive emails about new job opportunities. You can still see them in your portal.</p>
            )}
            <Button variant="outline" className="w-full" data-testid="unsub-toggle-back" onClick={() => submit(done !== "resubscribed")}>
              {done === "resubscribed" ? "Unsubscribe again" : "Resubscribe"}
            </Button>
          </div>
        )}

        {info && !done && !error && (
          <div className="space-y-4" data-testid="unsub-form">
            <p className="text-sm text-slate-600">Managing preferences for <b>{info.email}</b>. Required account, billing, legal and security emails are always sent and can't be turned off.</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="scope" checked={scope === "all"} onChange={() => setScope("all")} data-testid="unsub-scope-all" />
                Unsubscribe from <b>all optional email</b>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="scope" checked={scope === "category"} onChange={() => setScope("category")} data-testid="unsub-scope-category" />
                Just this type:
                <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={scope !== "category"} data-testid="unsub-category" className="ml-1 rounded-md border border-input bg-background px-2 py-1 text-sm">
                  {(info.categories || []).map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
                </select>
              </label>
            </div>
            <Button className="w-full bg-slate-900 text-white hover:bg-slate-800" disabled={busy} data-testid="unsub-confirm" onClick={() => submit(false)}>
              Confirm unsubscribe
            </Button>
            <Button variant="outline" className="w-full" disabled={busy} data-testid="unsub-resub" onClick={() => submit(true)}>
              Resubscribe instead
            </Button>
          </div>
        )}

        {!info && !error && <p className="text-sm text-slate-400">Loading…</p>}
      </div>
    </div>
  );
}
